import React, { useState, useCallback, useEffect, useRef } from "react";
import { PrimaryBlueButton, Severity } from "@velaro/velaro-component-lib";
import useToastAlert from "@/hooks/useToastAlert";
import { YouTubeSearchVideo } from "./types";

interface YouTubeChannelSearchProps {
  scraperApiUrl: string;
  getHeaders: () => Record<string, string>;
  onExtractTranscripts: (videos: { url: string; name: string }[]) => Promise<void>;
}

export default function YouTubeChannelSearch({
  scraperApiUrl,
  getHeaders,
  onExtractTranscripts
}: YouTubeChannelSearchProps) {
  const [channelId, setChannelId] = useState("");
  const [query, setQuery] = useState("");
  const [maxResults, setMaxResults] = useState(10);
  const [searching, setSearching] = useState(false);
  const [videos, setVideos] = useState<YouTubeSearchVideo[]>([]);
  const [selectedVideoIds, setSelectedVideoIds] = useState<Set<string>>(new Set());
  const [hasSearched, setHasSearched] = useState(false);
  const [extracting, setExtracting] = useState(false);
  const toastAlert = useToastAlert();

  // Credential state
  const [configured, setConfigured] = useState<boolean | null>(null); // null = loading
  const [credentialSource, setCredentialSource] = useState<string | null>(null);
  const [projectId, setProjectId] = useState<string | null>(null);
  const [showSetup, setShowSetup] = useState(false);
  const [credentialJson, setCredentialJson] = useState("");
  const [saving, setSaving] = useState(false);

  // Keep a ref to the latest getHeaders so the credentials effect doesn't
  // re-fire every time the parent recreates the function (e.g. after site loads).
  const getHeadersRef = useRef(getHeaders);
  useEffect(() => { getHeadersRef.current = getHeaders; });

  // Check credentials on mount
  useEffect(() => {
    fetch(`${scraperApiUrl}/config/credentials`, { headers: getHeadersRef.current() })
      .then(r => r.ok ? r.json() : { google_configured: false })
      .then(d => {
        setConfigured(d.google_configured);
        setCredentialSource(d.google_source || null);
        setProjectId(d.google_project_id || null);
      })
      .catch(() => setConfigured(false));
  }, [scraperApiUrl]);

  const saveCredentials = async () => {
    if (!credentialJson.trim()) return;
    setSaving(true);
    try {
      const response = await fetch(`${scraperApiUrl}/config/credentials`, {
        method: "POST",
        headers: getHeaders(),
        body: JSON.stringify({ google_service_account_json: credentialJson.trim() })
      });
      if (response.ok) {
        toastAlert.displayToast(Severity.Success, "Google API credentials saved");
        setConfigured(true);
        setCredentialSource("site");
        setShowSetup(false);
        setCredentialJson("");
        // Re-fetch to get project_id
        const statusResp = await fetch(`${scraperApiUrl}/config/credentials`, { headers: getHeaders() });
        if (statusResp.ok) {
          const data = await statusResp.json();
          setProjectId(data.google_project_id || null);
        }
      } else {
        const error = await response.json();
        console.error("Save credentials error:", error.detail);
        toastAlert.displayToast(Severity.Error, "Failed to save credentials");
      }
    } catch {
      toastAlert.displayToast(Severity.Error, "Failed to save credentials");
    } finally {
      setSaving(false);
    }
  };

  const removeCredentials = async () => {
    if (!confirm("Remove site-specific Google API credentials? Channel search will fall back to global credentials if available.")) return;
    try {
      const response = await fetch(`${scraperApiUrl}/config/credentials`, {
        method: "DELETE",
        headers: getHeaders()
      });
      if (response.ok) {
        toastAlert.displayToast(Severity.Success, "Credentials removed");
        // Re-check status (may fall back to global)
        const statusResp = await fetch(`${scraperApiUrl}/config/credentials`, { headers: getHeaders() });
        if (statusResp.ok) {
          const data = await statusResp.json();
          setConfigured(data.google_configured);
          setCredentialSource(data.google_source || null);
          setProjectId(data.google_project_id || null);
        } else {
          setConfigured(false);
          setCredentialSource(null);
          setProjectId(null);
        }
      }
    } catch {
      toastAlert.displayToast(Severity.Error, "Failed to remove credentials");
    }
  };

  const searchChannel = useCallback(async () => {
    if (!channelId.trim() || !query.trim()) return;

    setSearching(true);
    setHasSearched(true);
    setVideos([]);
    setSelectedVideoIds(new Set());

    try {
      const response = await fetch(`${scraperApiUrl}/youtube/search`, {
        method: "POST",
        headers: getHeaders(),
        body: JSON.stringify({
          channel_id: channelId.trim(),
          query: query.trim(),
          max_results: maxResults
        })
      });

      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          const safeVideos = Array.isArray(data.videos) ? data.videos : [];
          setVideos(safeVideos);
          if (safeVideos.length === 0) {
            toastAlert.displayToast(Severity.Warning, "No videos found matching your search");
          }
        } else {
          toastAlert.displayToast(Severity.Error, data.error || "Search failed");
        }
      } else {
        const error = await response.json();
        console.error("Search request error:", error.detail);
        toastAlert.displayToast(Severity.Error, "Search request failed");
      }
    } catch (error) {
      console.error("Channel search error:", error);
      toastAlert.displayToast(Severity.Error, "Failed to search channel");
    } finally {
      setSearching(false);
    }
  }, [channelId, query, maxResults, scraperApiUrl, getHeaders, toastAlert]);

  const toggleVideo = (videoId: string) => {
    setSelectedVideoIds(prev => {
      const next = new Set(prev);
      if (next.has(videoId)) {
        next.delete(videoId);
      } else {
        next.add(videoId);
      }
      return next;
    });
  };

  const selectAll = () => {
    setSelectedVideoIds(new Set(videos.map(v => v.video_id)));
  };

  const deselectAll = () => {
    setSelectedVideoIds(new Set());
  };

  const handleExtract = async () => {
    const selected = videos.filter(v => selectedVideoIds.has(v.video_id));
    if (selected.length === 0) return;

    setExtracting(true);
    try {
      await onExtractTranscripts(selected.map(v => ({ url: v.url, name: v.title })));
    } finally {
      setExtracting(false);
    }
  };

  const formatDate = (dateStr: string) => {
    try {
      return new Date(dateStr).toLocaleDateString();
    } catch {
      return dateStr;
    }
  };

  // Loading state
  if (configured === null) {
    return (
      <div className="mb-6 bg-white rounded-lg border p-4">
        <div className="flex items-center gap-2 text-sm text-slate-400">
          <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
          </svg>
          Checking YouTube API configuration...
        </div>
      </div>
    );
  }

  // Not configured — show setup prompt
  if (!configured && !showSetup) {
    return (
      <div className="mb-6 bg-white rounded-lg border p-4">
        <h3 className="text-sm font-semibold text-slate-700 mb-2">YouTube Channel Search</h3>
        <p className="text-xs text-slate-400 mb-3">
          Search a YouTube channel for videos and batch-extract transcripts for AI training.
          This feature requires Google API credentials (YouTube Data API v3).
        </p>
        <PrimaryBlueButton
          label="Configure YouTube API"
          onClick={() => setShowSetup(true)}
        />
      </div>
    );
  }

  // Setup form
  if (!configured && showSetup) {
    return (
      <div className="mb-6 bg-white rounded-lg border p-4">
        <h3 className="text-sm font-semibold text-slate-700 mb-2">Configure YouTube API</h3>
        <p className="text-xs text-slate-400 mb-3">
          Paste your Google Service Account JSON below. You can get this from{" "}
          <a href="https://console.cloud.google.com/iam-admin/serviceaccounts" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">
            Google Cloud Console
          </a>
          {" "}(IAM &gt; Service Accounts &gt; Keys &gt; Create key &gt; JSON).
          Make sure "YouTube Data API v3" is enabled in your project.
        </p>
        <textarea
          value={credentialJson}
          onChange={(e) => setCredentialJson(e.target.value)}
          placeholder='{"type": "service_account", "project_id": "...", ...}'
          rows={6}
          className="w-full px-3 py-2 border border-slate-300 rounded-md text-xs font-mono focus:outline-none focus:ring-2 focus:ring-blue-500 mb-3"
        />
        <div className="flex gap-2">
          <PrimaryBlueButton
            label={saving ? "Saving..." : "Save Credentials"}
            disabled={saving || !credentialJson.trim()}
            onClick={saveCredentials}
          />
          <button
            onClick={() => { setShowSetup(false); setCredentialJson(""); }}
            className="px-4 py-2 text-sm text-slate-600 hover:text-slate-800"
          >
            Cancel
          </button>
        </div>
      </div>
    );
  }

  // Configured — show search form
  return (
    <div className="mb-6 bg-white rounded-lg border p-4">
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-sm font-semibold text-slate-700">YouTube Channel Search</h3>
        <div className="flex items-center gap-2">
          {projectId && (
            <span className="text-xs text-slate-400">
              Project: {projectId}
            </span>
          )}
          {credentialSource === "site" && (
            <button
              onClick={removeCredentials}
              className="text-xs text-red-500 hover:text-red-700"
            >
              Remove credentials
            </button>
          )}
        </div>
      </div>
      <p className="text-xs text-slate-400 mb-3">
        Search a YouTube channel for videos and batch-extract transcripts for AI training.
      </p>

      {/* Search Form */}
      <div className="space-y-3 mb-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Channel ID</label>
            <input
              type="text"
              value={channelId}
              onChange={(e) => setChannelId(e.target.value)}
              placeholder="UCxxxxxxxxxxxxxxxxxxxxxx"
              className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
            <p className="text-xs text-slate-400 mt-1">
              YouTube channel ID (starts with UC). Find it on the channel's About page.
            </p>
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Search Query</label>
            <input
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Enter search terms..."
              className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              onKeyDown={(e) => {
                if (e.key === "Enter" && channelId.trim() && query.trim() && !searching) {
                  searchChannel();
                }
              }}
            />
          </div>
        </div>
        <div className="flex items-end gap-3">
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Max Results</label>
            <select
              value={maxResults}
              onChange={(e) => setMaxResults(Number(e.target.value))}
              className="px-3 py-2 border border-slate-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value={5}>5</option>
              <option value={10}>10</option>
              <option value={25}>25</option>
              <option value={50}>50</option>
            </select>
          </div>
          <PrimaryBlueButton
            label={searching ? "Searching..." : "Search Channel"}
            disabled={searching || !channelId.trim() || !query.trim()}
            onClick={searchChannel}
          />
        </div>
      </div>

      {/* Results */}
      {hasSearched && !searching && videos.length > 0 && (
        <div>
          {/* Selection controls */}
          <div className="flex items-center justify-between mb-3 pb-2 border-b">
            <div className="flex items-center gap-3">
              <span className="text-sm text-slate-600">
                {videos.length} video{videos.length !== 1 ? "s" : ""} found
              </span>
              <button
                onClick={selectAll}
                className="text-xs text-blue-600 hover:text-blue-800"
              >
                Select all
              </button>
              <button
                onClick={deselectAll}
                className="text-xs text-slate-500 hover:text-slate-700"
              >
                Deselect all
              </button>
            </div>
            {selectedVideoIds.size > 0 && (
              <PrimaryBlueButton
                label={extracting ? "Extracting..." : `Extract Transcripts (${selectedVideoIds.size})`}
                disabled={extracting}
                onClick={handleExtract}
              />
            )}
          </div>

          {/* Video cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
            {videos.map((video) => (
              <div
                key={video.video_id}
                role="button"
                tabIndex={0}
                onClick={() => toggleVideo(video.video_id)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    toggleVideo(video.video_id);
                  }
                }}
                className={`relative border rounded-lg p-3 cursor-pointer transition-all hover:shadow-md ${
                  selectedVideoIds.has(video.video_id)
                    ? "border-blue-500 bg-blue-50 ring-1 ring-blue-500"
                    : "border-slate-200 hover:border-slate-300"
                }`}
              >
                {/* Checkbox */}
                <div className="absolute top-2 right-2">
                  <input
                    type="checkbox"
                    checked={selectedVideoIds.has(video.video_id)}
                    onChange={() => toggleVideo(video.video_id)}
                    onClick={(e) => e.stopPropagation()}
                    tabIndex={-1}
                    className="h-4 w-4 text-blue-600 rounded border-slate-300"
                  />
                </div>

                {/* Thumbnail */}
                {video.thumbnail && (
                  <div className="mb-2 rounded overflow-hidden bg-slate-100">
                    <img
                      src={video.thumbnail}
                      alt={video.title}
                      className="w-full h-auto object-cover"
                      loading="lazy"
                    />
                  </div>
                )}

                {/* Title */}
                <h4 className="text-sm font-medium text-slate-800 mb-1 line-clamp-2 pr-6">
                  {video.title}
                </h4>

                {/* Meta */}
                <p className="text-xs text-slate-500 mb-1">{video.channel_title}</p>
                <p className="text-xs text-slate-400">{formatDate(video.published_at)}</p>

                {/* Description preview */}
                {video.description && (
                  <p className="text-xs text-slate-400 mt-1 line-clamp-2">
                    {video.description}
                  </p>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Empty state */}
      {hasSearched && !searching && videos.length === 0 && (
        <p className="text-sm text-slate-400 text-center py-4">
          No videos found. Try a different search query or check the channel ID.
        </p>
      )}
    </div>
  );
}
